import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {Company, Country, Order} from "../../../store/state/order.state";
import {CompanyAddressFormService} from "./company-address-form-service";
import {FormGroup} from "@angular/forms";
import {StandaloneFormComponent} from "../../../umbrella-common/components/standalone-form.component";
import {Actions, ofType} from "@ngrx/effects";
import {OrderActions} from "../../../store/actions/order.actions";
import {CompanyAddressBillingFormService} from "./company-address-billing-form-service";
import {filter, map} from "rxjs/operators";
import {TranslateService} from "@ngx-translate/core";
import {Observable} from "rxjs";
import {ConfigurationProvider} from "../../../configuration/configuration.provider";

@Component({
    selector: 'company-address',
    templateUrl: './company-address.template.html',
    styleUrls: ['./company-address.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CompanyAddressComponent extends StandaloneFormComponent implements OnInit {

	@Input() order: Order;
	@Input() countries: Country[];
	@Input() preselectedUS: boolean = false;
	@Input() isBillingAddress = false;

	@Output() isFormValidEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

	stringCountries: string[];
	selectedCountry: Country;
	form: FormGroup;
	formService: CompanyAddressFormService | CompanyAddressBillingFormService;

	constructor(private addressFormService: CompanyAddressFormService,
				private billingAddressFormService: CompanyAddressBillingFormService,
				private actions$: Actions,
				private translationService: TranslateService,
				private configProvider: ConfigurationProvider) {
		super(null);
	}

	ngOnInit(): void {
		if (this.isBillingAddress) {
			this.formService = this.billingAddressFormService;
			this.selectedCountry = this.countries.find(country => country.name == this.order.billingInformations.billingCompany.country) || this.countries.find(country => country.code == 'US');
		} else {
			this.formService = this.addressFormService;
			this.selectedCountry = this.countries.find(country => country.name == this.order.company.country) || this.countries.find(country => country.code == this.getCurrentLang());
		}
		this.setStandaloneFormService(this.formService);
		this.form = this.formService.buildStandaloneForm(this.order, this.countries, this.selectedCountry);
		this.stringCountries = this.countryCodes();

		this.actions$.pipe(ofType(OrderActions.SET_COUNTRY),
			filter(() => !this.isBillingAddress))
			.subscribe(value => {
				this.selectedCountry = this.countries.find(country => country.name == value.countryName);
			});

		this.actions$.pipe(ofType(OrderActions.SET_BILLING_COUNTRY),
			filter(() => this.isBillingAddress))
			.subscribe(value => {
				this.selectedCountry = this.countries.find(country => country.name == value.countryName);
			});

		this.formService.isFormValid().subscribe(isValid => this.isFormValidEvent.emit(isValid))
	}

	private getCurrentLang(): string {
		return this.translationService.currentLang.split('-')[1];
	}

	get placeholderText(): string {
		if (this.selectedCountry.code == 'CA') {
			return 'M6K 1V2';
		} else {
			return this.translationService.instant("company-address.zip-placeholder");
		}
	}

	private countryCodes(): string[] {
		const parentCountry = this.countries.find(country => this.selectedCountry?.code === country.dependentCountries.find(c => c.code)?.code);
		const countries = this.selectedCountry.dependentCountries != undefined ?
			[this.selectedCountry.code].concat(this.selectedCountry.dependentCountries.map(c => c.code)) : [this.selectedCountry.code];

		if (parentCountry) {
			countries.push(parentCountry.code);
		}
		return countries;
	}

	get usSelected(): boolean {
		return this.selectedCountry.code == 'US';
	}

	getCompanyAddress(): Company {
		return this.billingAddressFormService.getCompanyAddress();
	}

	includeTaxId(): Observable<boolean> {
		return this.configProvider.getConfigurationFor(this.selectedCountry.code).pipe(map(config => config.clientCountryConfig?.order.additionalParamsIncluded['taxId']));
	}

	includeKvkNr(): Observable<boolean> {
		return this.configProvider.getConfigurationFor(this.selectedCountry.code).pipe(map(config => config.clientCountryConfig?.order.additionalParamsIncluded['kvkNr']));
	}

	correctZipCode(): void  {
		this.configProvider.getConfigurationFor(this.selectedCountry.code).pipe(map(config => config.clientCountryConfig.order.zipCodeCorrector), filter(correctorFcn => correctorFcn != null))
			.subscribe(correctorFcn => {
				let zipCode = this.getValueFor('zipCode');
				let corrected = correctorFcn(zipCode);
				this.updateFormControl('zipCode', corrected);
			})
	}
}
